import React, { useState, useMemo } from 'react';
import { Header, Footer, ChannelCard } from './shared';
import { TV_CHANNELS, getCategories } from '../config';
import { useDevice } from '../hooks';
import type { Channel, ChannelCategory } from '../types';

const TVChannels: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<'All' | ChannelCategory>('All');
    useDevice(); // For future responsive enhancements

    const categories = useMemo(() => ['All' as const, ...getCategories()], []);

    const filteredChannels = useMemo(() => {
        return TV_CHANNELS.filter(channel => {
            const matchesSearch = channel.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'All' || channel.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [searchTerm, selectedCategory]);

    // Group channels by category for display
    const channelsByCategory = useMemo(() => {
        if (selectedCategory !== 'All' || searchTerm) {
            return { [selectedCategory === 'All' ? 'Results' : selectedCategory]: filteredChannels };
        }

        const grouped: Record<string, Channel[]> = {};
        filteredChannels.forEach(channel => {
            if (!grouped[channel.category]) {
                grouped[channel.category] = [];
            }
            grouped[channel.category].push(channel);
        });
        return grouped;
    }, [filteredChannels, selectedCategory, searchTerm]);

    return (
        <div className="min-h-screen bg-dark-950">
            <Header
                onSearch={setSearchTerm}
                searchValue={searchTerm}
                showSearch={true}
            />

            {/* Hero Section */}
            <section className="relative py-12 sm:py-16 lg:py-20 overflow-hidden">
                {/* Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-rose-500/10 via-dark-950 to-purple-500/10" />
                <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.15) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)`,
                }} />

                <div className="relative max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
                    <div className="text-center mb-10 sm:mb-12 lg:mb-16">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black mb-4
              animate-fade-in">
                            <span className="text-gradient">Live TV</span> Channels
                        </h1>
                        <p className="text-gray-400 text-lg sm:text-xl lg:text-2xl max-w-2xl mx-auto animate-fade-in"
                            style={{ animationDelay: '100ms' }}>
                            Watch your favorite channels live, anytime, anywhere
                        </p>
                    </div>

                    {/* Category Filter */}
                    <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 animate-fade-in"
                        style={{ animationDelay: '200ms' }}>
                        {categories.map((category, index) => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`
                  px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold
                  text-sm sm:text-base
                  transition-all duration-300
                  ${selectedCategory === category
                                        ? 'bg-gradient-to-r from-primary-500 to-rose-500 text-white shadow-lg shadow-primary-500/30'
                                        : 'bg-dark-800 text-gray-400 hover:text-white hover:bg-dark-700 border border-white/10'
                                    }
                `}
                                style={{ animationDelay: `${(index + 3) * 50}ms` }}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* Stats */}
                    <div className="flex justify-center gap-8 sm:gap-12 lg:gap-16 mb-10
            text-center animate-fade-in" style={{ animationDelay: '300ms' }}>
                        <div>
                            <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient">
                                {TV_CHANNELS.length}
                            </p>
                            <p className="text-gray-500 text-sm sm:text-base">Channels</p>
                        </div>
                        <div>
                            <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient">
                                {categories.length - 1}
                            </p>
                            <p className="text-gray-500 text-sm sm:text-base">Categories</p>
                        </div>
                        <div>
                            <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient">
                                24/7
                            </p>
                            <p className="text-gray-500 text-sm sm:text-base">Live</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Channels Grid */}
            <main className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 pb-20">
                {Object.entries(channelsByCategory).length > 0 ? (
                    Object.entries(channelsByCategory).map(([category, channels], catIndex) => (
                        <section key={category} className="mb-12 lg:mb-16">
                            {/* Category Header */}
                            {(selectedCategory === 'All' && !searchTerm) && (
                                <div className="flex items-center gap-4 mb-6 lg:mb-8">
                                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">
                                        {category}
                                    </h2>
                                    <div className="flex-grow h-px bg-gradient-to-r from-white/20 to-transparent" />
                                    <span className="text-gray-500 text-sm">
                                        {channels.length} channel{channels.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            )}

                            {/* Channels */}
                            <div className="channel-grid">
                                {channels.map((channel, index) => (
                                    <ChannelCard
                                        key={channel.id}
                                        channel={channel}
                                        index={catIndex * 10 + index}
                                    />
                                ))}
                            </div>
                        </section>
                    ))
                ) : (
                    <div className="text-center py-20">
                        <p className="text-gray-400 text-xl mb-4">
                            No channels found for "{searchTerm}"
                        </p>
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setSelectedCategory('All');
                            }}
                            className="text-primary-500 hover:text-primary-400 font-semibold"
                        >
                            Clear filters
                        </button>
                    </div>
                )}
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default TVChannels;
